import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PropTypes } from "prop-types";
import { ANALYSIS_KEYS } from "data/models/AnalysisModel";
import { setSimulationsVisible } from "store/appSlice";
import {
  addLayer,
  selectOAuthCredentials,
  setViewState,
} from "@carto/react-redux";
import { setSelectedLocation, setSimulations } from "store/appSlice";

import {
  CircularProgress,
  debounce,
  Divider,
  InputBase,
  List,
  ListItem,
  ListItemText,
  Paper,
  SvgIcon,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { selectName } from "store/appSlice";
import { geocodeStreetPoint } from "data/models/GeocoderModel";
import { useDataFetch } from "hooks/useDataFetch";
// import { getAssets } from 'data/models/AssetsModel';
import { useNavigate } from "react-router";

const DEFAULT_COUNTRY = "UNITED KINGDOM"; // 'SPAIN', 'USA'

const setGeocoderResult = (payload) => ({
  type: "carto/setGeocoderResult",
  payload,
});

const useStyles = makeStyles((theme) => ({
  paperInput: {
    height: theme.spacing(4.5),
    minWidth: theme.spacing(10),
    paddingLeft: theme.spacing(1.5),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    transition: "min-width 0.25s ease-in-out",
    "&:focus-within": {
      minWidth: theme.spacing(75),
    },
  },
  notFound: {
    animation: "$shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both",
    transform: "translate3d(0, 0, 0)",
  },
  "@keyframes shake": {
    "10%, 90%": {
      transform: "translate3d(-1px, 0, 0)",
    },
    "20%, 80%": {
      transform: "translate3d(2px, 0, 0)",
    },
    "30%, 50%, 70%": {
      transform: "translate3d(-4px, 0, 0)",
    },
    "40%, 60%": {
      transform: "translate3d(4px, 0, 0)",
    },
  },
  icon: {
    fill: theme.palette.text.secondary,
    height: "1em",
    fontSize: `${theme.typography.body2.lineHeight}em`,
  },
  clear: {
    ...theme.typography.body2,
  },
  input: {
    ...theme.typography.body2,
    width: `calc(100% - ${theme.spacing(5)}px)`,
    marginLeft: theme.spacing(1),
  },
  results: {
    backgroundColor: theme.palette.common.white,
    position: "absolute",
    top: theme.spacing(5),
    width: "100%",
    marginLeft: theme.spacing(-1.5),
  },
  locationButton: {
    cursor: "pointer",
  },
  // hasAssets: {
  //   marginTop: theme.spacing(1),
  // },
}));

const SearchIcon = (args) => (
  <SvgIcon {...args}>
    <path
      d="M11,4 C14.8659932,4 18,7.13400675 18,11 C18,12.7003211 17.3937669,14.2590489 16.3856562,15.4718279 L19.4748737,18.5606602 L18.0606602,19.9748737 L14.8998887,16.8138615 C13.7854137,17.5629194 12.4437497,18 11,18 C7.13400675,18 4,14.8659932 4,11 C4,7.13400675 7.13400675,4 11,4 Z M11,6 C8.23857625,6 6,8.23857625 6,11 C6,13.7614237 8.23857625,16 11,16 C13.7614237,16 16,13.7614237 16,11 C16,8.23857625 13.7614237,6 11,6 Z"
      fill="inherit"
    ></path>
  </SvgIcon>
);

/**
 * Renders a <GeocoderWidget /> component
 * @param  {object} props
 * @param  {Object} [props.className] - Material-UI withStyle class for styling
 * @param  {Function} [props.onError] - Function to handle error messages from the widget.
 */
function GeocoderWidget(props) {
  const classes = useStyles();
  const inputRef = useRef();
  //const oauthCredentials = useSelector(selectOAuthCredentials);
  //const globalCredentials = useSelector((state) => state.carto.credentials);
  //const name = useSelector(selectName('assets'));
  //const credentials = oauthCredentials || globalCredentials;
  const credentials = useSelector((state) => state.carto.credentials2);
  // Component local state and events handling
  const [searchText, setSearchText] = useState("");
  const [loading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Actions dispatched
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const slowSetSearchText = debounce(setSearchText, 0);

  useEffect(() => {
    // layer to display the geocoded direction marker
    dispatch(
      addLayer({
        id: "geocoderLayer",
      })
    );
  }, [dispatch]);

  // const canLoadAssets = searchText.length >= 2;

  // const { data: assets, loading: assetsLoading } = useDataFetch({
  //   initialData: [],
  //   use404: false,
  //   enabled: canLoadAssets,
  //   errorMsg: `[GeocoderWidget.js] Error fetching`,
  //   callback: useCallback(
  //     ({ metadata, credentials, abortController }) => {
  //       return getAssets(metadata, credentials, {
  //         searchName: searchText,
  //         searchId: searchText,
  //         limit: 5,
  //         abortController,
  //       });
  //     },
  //     [searchText]
  //   ),
  // });

  const handleChange = (e) => {
    slowSetSearchText(e.target.value);
  };

  const handleInput = (e) => {
    // if (e.target.value === '') {
    //   updateMarker(null);
    // }
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === 13) {
      // Force blur to hide virtual keyboards on mobile and search
      handleSearch();
      e.target.blur();
    }
  };

  const handleSearch = async () => {
    if (credentials) {
      try {
        setIsLoading(true);
        const result = await geocodeStreetPoint(credentials, {
          searchText,
          country: DEFAULT_COUNTRY,
        });
        if (result) {
          zoomToResult(result);
          updateMarker(result);
          dispatch(setSelectedLocation(result));
          dispatch(setSimulations(ANALYSIS_KEYS));
          dispatch(setSimulationsVisible(true));
          slowSetSearchText("");
          //dispatch(setSearchText(''));
        } else {
          setNotFound(true);
          setTimeout(() => setNotFound(false), 5000);
        }
      } catch (e) {
        handleGeocodeError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const zoomToResult = (result) => {
    dispatch(
      setViewState({
        longitude: result.longitude,
        latitude: result.latitude,
        zoom: 12,
        hasFlight: true,
        transitionDuration: 500,
        dragRotate: true,
      })
    );
  };

  const updateMarker = (result) => {
    dispatch(setGeocoderResult(result));
  };

  const handleGeocodeError = (error) => {
    if (props.onError) {
      props.onError(error);
    }
  };

  // const handleSelectAsset = (asset) => {
  //   navigate(`/explore/assets/${asset.cartodb_id}`);
  //   setSearchText('');
  // };

  return (
    <Paper
      className={`${props.className} ${classes.paperInput} ${
        notFound ? classes.notFound : ""
      }`}
      elevation={2}
    >
      {loading ? (
        <CircularProgress size={20} className={classes.icon} />
      ) : (
        <SearchIcon className={classes.icon} />
      )}
      <InputBase
        type="search"
        tabIndex={-1}
        inputRef={inputRef}
        size="small"
        placeholder={`Search address`}
        className={classes.input}
        inputProps={{ "aria-label": "GeocoderSearch" }}
        onChange={handleChange}
        onInput={handleInput}
        onKeyDown={handleKeyPress}
      />
      {searchText && (
        <Paper elevation={3} className={classes.results}>
          <List>
            <ListItem
              className={`${classes.locationButton}`}
              // click={handleSearch}
            >
              <ListItemText>
                Press the <span className="bold"> Enter Key ⏎ </span> to find
                location.
              </ListItemText>
            </ListItem>
          </List>
        </Paper>
      )}
    </Paper>
  );
}

GeocoderWidget.propTypes = {
  className: PropTypes.string,
  onError: PropTypes.func,
};

GeocoderWidget.defaultProps = {};

export default GeocoderWidget;
